import React, { useRef, useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import Sidenav from "../components/Sidenav";
import "../Console.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const IRIS_KEY = "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43";

const BleSTInvoice = ({ domain, userData }) => {
  const invoiceRef = useRef();
  const [assignment, setAssignment] = useState(null);
  const [clientName, setClientName] = useState("");
  const [loading, setLoading] = useState(true);
  const [assignmentId, setAssignmentId] = useState(null);
  const [allOrgs, setAllOrgs] = useState([]);

  useEffect(() => {
    fetch("/api/organizations")
      .then((r) => r.json())
      .then((data) => setAllOrgs(data || []))
      .catch(() => {});
  }, []);

  const clientOrgDetails = useMemo(() => {
    if (!allOrgs.length || !assignment) return null;
    const clientId = assignment.client?._id;
    if (clientId) {
      const byId = allOrgs.find((o) => o._id === clientId);
      if (byId) return byId;
    }
    return allOrgs.find((o) => o.organization === clientName) || null;
  }, [allOrgs, assignment, clientName]);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    window.addEventListener("contextmenu", handleContextMenu);
    return () => window.removeEventListener("contextmenu", handleContextMenu);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("assignment");
    setAssignmentId(id);

    if (!id) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const assignRes = await fetch(
          "https://api.iriscrypt.com/orderAssignment",
          {
            headers: { "x-api-key": IRIS_KEY },
          },
        );
        const allAssignments = await assignRes.json();
        const found = (allAssignments || []).find((a) => a._id === id);
        setAssignment(found || null);
        setClientName(found?.client?.name || "");
      } catch (error) {
        console.error("Failed to load invoice data:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDownload = async () => {
    if (!invoiceRef.current || !assignment) return;
    const canvas = await html2canvas(invoiceRef.current, {
      scale: 2,
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`blest-invoice-${assignment.minSerial}.pdf`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const quantity = assignment
    ? assignment.maxSerial - assignment.minSerial + 1
    : 0;

  const fmtNum = (n) =>
    Number.isFinite(n)
      ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "0.00";

  const pricing = (() => {
    if (!assignment?.currency) return null;
    const unit = parseFloat(assignment.unitCost) || 0;
    const additionals = (assignment.additionalCosts || []).filter(
      (c) => c.label && c.amount != null && c.amount !== "",
    );
    const addonsTotal = additionals.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    const unitSubtotal = unit * quantity;
    const subtotal = unitSubtotal + addonsTotal;
    const discountPct = parseFloat(assignment.discount) || 0;
    const discountAmt = subtotal * discountPct / 100;
    const afterDiscount = subtotal - discountAmt;
    const taxPct = parseFloat(assignment.tax) || 0;
    const taxAmt = afterDiscount * taxPct / 100;
    const total = afterDiscount + taxAmt;
    return { unit, additionals, unitSubtotal, subtotal, discountPct, discountAmt, afterDiscount, taxPct, taxAmt, total };
  })();

  const cell = { padding: "10px", border: "1px solid #ddd" };
  const cellR = { padding: "10px", border: "1px solid #ddd", textAlign: "right" };

  return (
    <>
      <Helmet>
        <title>BleST Invoice | BLEEP</title>
      </Helmet>

      <div className="d-flex">
        <Sidenav domain={domain} />
        <div
          style={{
            flex: 1,
            position: "relative",
            height: "calc(100vh - 94px)",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background: "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,153,1,1) 100%)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute", inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat", backgroundSize: "400px 400px",
                backgroundPosition: "center", mixBlendMode: "overlay",
                opacity: 0.08, zIndex: 0, pointerEvents: "none",
              }}
            />
            <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <button
                  onClick={() => window.history.back()}
                  style={{
                    display: "flex", alignItems: "center", gap: 7,
                    background: "rgba(255,255,255,0.18)", border: "1.5px solid rgba(255,255,255,0.35)",
                    color: "#fff", borderRadius: 10, padding: "8px 16px",
                    fontWeight: 700, fontSize: 13, cursor: "pointer",
                    backdropFilter: "blur(4px)", flexShrink: 0,
                  }}
                >
                  <i className="fa-solid fa-arrow-left" style={{ fontSize: 12 }} />
                  Back
                </button>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <i className="fa-solid fa-file-invoice-dollar" style={{ fontSize: 22, filter: "drop-shadow(1px 2px 0px #1c1c1c)" }} />
                    <b style={{ fontSize: 26, textShadow: "#1c1c1c 1px 2px 0px", lineHeight: 1.1 }}>
                      BleST Invoice
                    </b>
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", textShadow: "#1c1c1c 1px 1px 0px", paddingLeft: 2 }}>
                    Tag Assignment Invoice
                  </div>
                </div>
              </div>
              {assignment && (
                <button
                  onClick={handleDownload}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "#fff", border: "none", color: "#c13d00",
                    borderRadius: 30, padding: "10px 22px",
                    fontWeight: 700, fontSize: 14, cursor: "pointer",
                    boxShadow: "rgba(0,0,0,0.25) 0px 3px 8px", flexShrink: 0,
                  }}
                >
                  <i className="fa-solid fa-download" style={{ fontSize: 13 }} />
                  Download PDF
                </button>
              )}
            </div>
          </header>

          {/* Body */}
          <div
            style={{
              padding: "20px",
              backgroundColor: "#ebebeb",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {loading ? (
              <div>Loading...</div>
            ) : !assignmentId ? (
              <div style={{ color: "red" }}>
                No assignment ID provided in URL.
              </div>
            ) : !assignment ? (
              <div style={{ color: "red" }}>Error loading assignment data.</div>
            ) : (
              <div
                ref={invoiceRef}
                style={{
                  width: "21cm",
                  minHeight: "29.7cm",
                  background: "white",
                  padding: "40px",
                  fontFamily: "Arial, sans-serif",
                  fontSize: "12pt",
                  color: "#333",
                }}
              >
                {/* Logo */}
                <div style={{ textAlign: "right", marginBottom: "30px" }}>
                  <img
                    src="/images/blest_logo.png"
                    alt="BleST Logo"
                    style={{ height: "80px" }}
                  />
                </div>

                {/* Addresses */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "30px",
                  }}
                >
                  <div style={{ lineHeight: 1.7 }}>
                    <b>BILL TO:</b>
                    <br />
                    {clientName || <em style={{ color: "#999" }}>Client</em>}
                    {clientOrgDetails?.address && <><br />{clientOrgDetails.address}</>}
                    {clientOrgDetails?.contactEmail && <><br />{clientOrgDetails.contactEmail}</>}
                    {clientOrgDetails?.primaryPhone && <><br />{clientOrgDetails.primaryPhone}</>}
                    {clientOrgDetails?.secondaryPhone && <><br />{clientOrgDetails.secondaryPhone}</>}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <b>INVOICE DATE:</b>{" "}
                    {formatDate(assignment.date || new Date())}
                    <br />
                    <b>INVOICE #:</b> BLEST-
                    {assignment._id.slice(-6).toUpperCase()}
                  </div>
                </div>

                <hr style={{ margin: "20px 0" }} />

                {/* Order Details */}
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "20px",
                  }}
                >
                  <tbody>
                    <tr>
                      <td style={cell}>
                        <b>Tag Type</b>
                      </td>
                      <td style={cell}>{assignment.type}</td>
                      <td style={cell}>
                        <b>Tag Length</b>
                      </td>
                      <td style={cell}>{assignment.length}mm</td>
                    </tr>
                    <tr>
                      <td style={cell}>
                        <b>Serial Range</b>
                      </td>
                      <td style={cell}>
                        {assignment.minSerial} - {assignment.maxSerial}
                      </td>
                      <td style={cell}>
                        <b>Quantity</b>
                      </td>
                      <td style={cell}>{quantity.toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td style={cell}>
                        <b>Assignment Date</b>
                      </td>
                      <td
                        style={cell}
                        colSpan={3}
                      >
                        {(assignment.date || "").split("T")[0]}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Tag Assignments */}
                {assignment.tagAssignments &&
                  assignment.tagAssignments.length > 0 && (
                    <>
                      <p style={{ fontWeight: "bold", marginBottom: "6px" }}>
                        Tag Assignments
                      </p>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          marginBottom: "20px",
                          fontSize: "11pt",
                        }}
                      >
                        <thead>
                          <tr style={{ background: "#f5f5f5" }}>
                            <th style={cell}>Serial Range</th>
                            <th style={cell}>Item Code</th>
                            <th style={cell}>Metadata</th>
                            <th style={cell}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignment.tagAssignments.map((ta, i) => (
                            <tr key={i}>
                              <td style={cell}>
                                {ta.minSerial} - {ta.maxSerial}
                              </td>
                              <td style={cell}>{ta.itemCode || "-"}</td>
                              <td style={cell}>
                                {Object.entries(ta.metadata || {}).map(
                                  ([k, v]) => (
                                    <div key={k}>
                                      <b>{k}:</b> {v}
                                    </div>
                                  ),
                                )}
                              </td>
                              <td style={cell}>
                                {(ta.date || "").split("T")[0]}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}

                {/* Pricing */}
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "20px",
                  }}
                >
                  <tbody>
                    {pricing ? (
                      <>
                        {pricing.unit > 0 && (
                          <tr>
                            <td style={cell} colSpan={2}>
                              <b>Unit Cost</b>
                              <div style={{ fontSize: "10pt", color: "#666", marginTop: 2 }}>
                                {assignment.currency} {fmtNum(pricing.unit)} &times; {quantity.toLocaleString()} tags
                              </div>
                            </td>
                            <td style={cell}><b>Unit Subtotal</b></td>
                            <td style={cellR}>{assignment.currency} {fmtNum(pricing.unitSubtotal)}</td>
                          </tr>
                        )}
                        {pricing.additionals.map((c, i) => (
                          <tr key={i}>
                            <td style={cell} colSpan={3}>{c.label}</td>
                            <td style={cellR}>{assignment.currency} {fmtNum(parseFloat(c.amount) || 0)}</td>
                          </tr>
                        ))}
                        {(pricing.unit > 0 || pricing.additionals.length > 0) && (
                          <tr style={{ background: "#f9f9f9" }}>
                            <td style={cell} colSpan={3}><b>Subtotal</b></td>
                            <td style={cellR}><b>{assignment.currency} {fmtNum(pricing.subtotal)}</b></td>
                          </tr>
                        )}
                        {pricing.discountPct > 0 && (
                          <tr>
                            <td style={cell} colSpan={3}>Discount ({pricing.discountPct}%)</td>
                            <td style={{ ...cellR, color: "#16a34a" }}>
                              &minus; {assignment.currency} {fmtNum(pricing.discountAmt)}
                            </td>
                          </tr>
                        )}
                        {pricing.taxPct > 0 && (
                          <tr>
                            <td style={cell} colSpan={3}>VAT ({pricing.taxPct}%)</td>
                            <td style={cellR}>{assignment.currency} {fmtNum(pricing.taxAmt)}</td>
                          </tr>
                        )}
                      </>
                    ) : (
                      <>
                        <tr>
                          <td style={cell}><b>Unit Cost</b></td>
                          <td style={cell}>-</td>
                          <td style={cell}><b>Subtotal</b></td>
                          <td style={cell}>-</td>
                        </tr>
                        <tr>
                          <td style={cell}><b>Additional Charges</b></td>
                          <td style={cell} colSpan={3}>-</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>

                {/* Total */}
                <div style={{ textAlign: "right", marginTop: "10px" }}>
                  <h4 style={{ margin: 0 }}>
                    TOTAL AMOUNT:{" "}
                    {pricing
                      ? `${assignment.currency} ${fmtNum(pricing.total)}`
                      : "-"}
                  </h4>
                </div>

                <hr style={{ margin: "30px 0" }} />

                <div
                  style={{
                    textAlign: "right",
                    fontSize: "10pt",
                    color: "#555",
                  }}
                >
                  <b>Bleep Security Tags Limited</b>
                  <br />
                  Plot 40, Nakasero Road, Kampala, Uganda
                  <br />
                  Kampala, Uganda
                  <br />
                  +256752781782 | +256414257661
                  <br />
                  fred@acnafrica.org | blest@bleep.ug
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BleSTInvoice;
